import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import moment from 'moment'
import SearchSelect from '../SearchSelect'
import { getEquipTypes, getInvoices, getVendor, saveInvoice } from '../../service/client'
import round from '../../utils/round'
import generateInvoicesPdf from '../../utils/generateInvoicesPdf'
import { OFFICE_USER } from '../../constants/permissions.json'

const emptyRow = {
  equipmentType: '',
  hours: 0,
  rate: 0
}

export default function Invoice(props) {
  const { showModal, hideModal, user, timesheetId, setState, invoiceId } = props
  const [dueDate, setDueDate] = useState(moment())
  const [equipOptions, setEquipOptions] = useState([])
  const [invoiceDate, setInvoiceDate] = useState(moment())
  const [invoiceUsername, setInvoiceUsername] = useState()
  const [loading, setLoading] = useState(false)
  const [readOnly, setReadOnly] = useState(false)
  const [rows, setRows] = useState([emptyRow])
  const [vendorId, setVendorId] = useState()
  const [vendorInfo, setVendorInfo] = useState({})

  const { name, address, city, state, zip, phone, fax } = vendorInfo

  useEffect(() => {
    if (invoiceId) {
      setLoading(true)
      getInvoices(user, invoiceId).then(res => {
        const r = res.body[0]
        const rows = JSON.parse(r.lines).map(l => {
          return {
            equipmentType: { value: l.item, label: l.item },
            hours: l.hours_worked,
            rate: l.rate
          }
        })

        setReadOnly(true)
        setRows(rows)
        setDueDate(moment(r.due_date))
        setInvoiceDate(moment(r.invoice_date))
        setVendorId(r.vendor_id)
        setInvoiceUsername(r.username)
        setLoading(false)
      }).catch(console.log)
    }
  }, [invoiceId])
  
  useEffect(() => {
    if (vendorId) {
      getVendor(user, vendorId).then(r => {
        setVendorInfo(r.body[0])
      })
    }
  }, [vendorId])

  useEffect(() => {
    if (user.vendorIdd && !invoiceId) {
      setVendorId(user.vendorIdd)
    }
  }, [user.vendorIdd])

  function reset() {
    setRows([emptyRow])
    setInvoiceDate(moment())
    setDueDate(moment())
    setLoading(false)
    setReadOnly(false)
  }

  function setRowItem(idx, field, value) {
    const newRows = JSON.parse(JSON.stringify(rows))
    newRows[idx] = { ...newRows[idx], [field]: value }
    setRows(newRows)
  }

  function createSaveObj() {
    const saveObj = {
      invoiceDate: invoiceDate.format('YYYY-MM-DD HH:mm:ss Z'),
      dueDate: dueDate.format('YYYY-MM-DD HH:mm:ss Z'),
      timesheetId,
      vendorId: user.vendorIdd,
      username: user.username
    }

    saveObj.lines = rows.map(r => {
      return {
        item: r.equipmentType.value,
        hoursWorked: r.hours,
        rate: r.rate,
      }
    })

    return saveObj
  }

  function createPrintObj() {
    const prepObj = {
      address, city, state, zip, phone, fax,
      vendor: name,
      timesheetId,
      id: invoiceId,
      invoiceDate: invoiceDate.format('MM/DD/YYYY'),
      dueDate: dueDate.format('MM/DD/YYYY'),
      invoiceNumber: `${user.username}${invoiceDate.format('MMDDYYhhmmss')}`,
      username: invoiceUsername
    }

    prepObj.rows = rows.filter(r => r.item !== '').map(r => {
      return {
        rate: r.rate,
        hours: r.hours,
        amount: round(r.hours * r.rate),
        equipmentType: r.equipmentType.value
      }
    })
    prepObj.total = round(prepObj.rows.reduce((a, b) => a + b.amount, 0))

    return prepObj
  }

  useEffect(() => {
    getEquipTypes(user).then(r => {
      const equipOptions = r.body.map(e => {
        return {
          label: e.VendorEquipmentDescription,
          value: e.VendorEquipmentDescription,
        }
      })
      setEquipOptions(equipOptions)
    })
  }, [])

  function save() {
    setLoading(true)
    saveInvoice(user, createSaveObj()).then(() => {
      setState({
        showSnackbar: true,
        snackbarMsg: 'Invoice Submitted',
        snackbarSeverity: 'success',
        invoiceSubmitted: true
      })
      reset()
      hideModal()
    }).catch(e => {
      console.log(e)
      setState({
        showSnackbar: true,
        snackbarMsg: 'There was an error submitting the invoice',
        snackbarSeverity: 'error',
      })
      setLoading(false)
    });
  }

  function print() {
    setLoading(true)
    const printObj = createPrintObj()
    generateInvoicesPdf([printObj], user).then(() => {
      setLoading(false)
    })
      .catch(console.log)
  }

  function close() {
    reset()
    hideModal()
  }

  function filterOptions(search) {
    return Promise.resolve(equipOptions.filter(({ label }) => label.toLowerCase().includes(search.toLowerCase())).slice(0, 50))
  }

  const equipSelect = rows.map((r, idx) => (
    <div style={{ display: 'flex', gap: 20, justifyContent: 'space-between', alignItems: 'flex-end' }} key={idx}>
      <SearchSelect
        defaultOptions={equipOptions}
        value={rows[idx].equipmentType}
        onChange={e => setRowItem(idx, 'equipmentType', e)}
        loadOptions={filterOptions}
        disabled={readOnly}
      />
      <Input
        type='number'
        value={rows[idx].hours}
        onChange={e => setRowItem(idx, 'hours', e.target.value)}
        min={0}
        disabled={readOnly}
      />
      <Input
        type='number'
        value={rows[idx].rate}
        onChange={e => setRowItem(idx, 'rate', e.target.value)}
        min={0}
        disabled={readOnly}
      />
      <div style={{ width: 166, textAlign: 'right', borderBottom: '1px solid #949494' }}>{round(rows[idx].hours * rows[idx].rate)}</div>
    </div>
  ))

  return (
    <Dialog
      open={!!showModal}
      onClose={close}
      disableBackdropClick
      fullWidth={true}
      maxWidth={false}
    >
      <DialogTitle id="alert-dialog-title">{`${!invoiceId ? 'Create ' : ''}Invoice`}</DialogTitle>

      <DialogContent style={{ height: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>{name}</div>
            <div>{address}</div>
            <div>{city}, {state} {zip}</div>
            {phone && <div>Phone: {phone}</div>}
            {fax && <div>Fax: {fax}</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
              <div>
                Invoice Date
              </div>
              <KeyboardDatePicker
                {...{
                  style: { margin: 2, marginBottom: 4 },
                  value: invoiceDate,
                  format: 'MM/dd/yyyy',
                  onChange: (e) => setInvoiceDate(moment(e)),
                  InputAdornmentProps: { position: 'start', style: { marginRight: 0 } },
                  KeyboardButtonProps: { style: { padding: 2 } },
                  disabled: readOnly,
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
              <div>
                Due Date
              </div>
              <KeyboardDatePicker
                {...{
                  style: { margin: 2, marginBottom: 4 },
                  value: dueDate,
                  format: 'MM/dd/yyyy',
                  onChange: (e) => setDueDate(moment(e)),
                  InputAdornmentProps: { position: 'start', style: { marginRight: 0 } },
                  KeyboardButtonProps: { style: { padding: 2 } },
                  disabled: readOnly,
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
              <div>
                Time Sheet ID
              </div>
              <div style={{ width: 196 }}>
                {timesheetId}
              </div>
            </div>
          </div>

        </div>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 10 }}>
          <div style={{ flexGrow: 8 }}>
            Item
          </div>
          <div style={{ width: 166 }}>
            Hours
          </div>
          <div style={{ width: 166 }}>
            Rate
          </div>
          <div style={{ width: 166 }}>
            Amount
          </div>
        </div>
        {equipSelect}
        <Button onClick={() => setRows([...rows, emptyRow])}>
          New Line
        </Button>
      </DialogContent>

      <DialogActions>
        {!invoiceId ? (
          <Button onClick={save} color="primary" disabled={loading}>Submit</Button>
        ) : user.permissions.includes(OFFICE_USER) ? (
          <Button onClick={print} color="primary" disabled={loading}>Print</Button>
        ) : (
          <div></div>
        )}
        <Button onClick={close} color="primary">Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}